import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { VIAL_IMAGE } from "../data/products";
import { trackBeginCheckout, trackOrderRequest } from "../lib/analytics";
import { readRef } from "../lib/referrals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Checkout = () => {
    const {
        items,
        clear,
        subtotal,
        discountApplied,
        discountAmount,
        freeShippingApplied,
        shippingAmount,
        grandTotal,
        amountToDiscount,
    } = useCart();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        city: "",
        notes: "",
        company: "",
    });

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // Fire GA4 `begin_checkout` once per checkout session (when items are present).
    const beginCheckoutFiredRef = useRef(false);
    useEffect(() => {
        if (beginCheckoutFiredRef.current) return;
        if (submitted) return;
        if (!items || items.length === 0) return;
        trackBeginCheckout({ items, value: grandTotal, referralCode: readRef() });
        beginCheckoutFiredRef.current = true;
    }, [items, grandTotal, submitted]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please enter your full name and email.");
            return;
        }
        if (items.length === 0) {
            toast.error("Your cart is empty.");
            return;
        }
        setSubmitting(true);

        const payload = {
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            city: form.city.trim() || null,
            notes: form.notes || null,
            company: form.company,
            referral_code: readRef(),
            subtotal: Number(subtotal.toFixed(2)),
            discount: Number(discountAmount.toFixed(2)),
            shipping: Number(shippingAmount.toFixed(2)),
            total: Number(grandTotal.toFixed(2)),
            items: items.map((i) => ({
                product_id: i.id,
                name: i.name,
                dosage: i.dosage,
                quantity: i.quantity,
                price: Number(i.price),
                line_total: Number((Number(i.price) * i.quantity).toFixed(2)),
            })),
        };

        try {
            const { data } = await axios.post(`${API}/checkout`, payload);

            // Fire GA4 `order_request` with full ecommerce payload BEFORE clearing the cart.
            const transactionId =
                (data && (data.order_id || data.id || data.checkout_id)) ||
                `qgl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            trackOrderRequest({
                transactionId,
                items,
                value: grandTotal,
                shipping: shippingAmount,
                discount: discountAmount,
                coupon: discountApplied ? "AUTO_10_OFF" : undefined,
                referralCode: readRef(),
            });

            if (data && data.email_sent === false) {
                toast.success(
                    "Order received — your inquiry has been saved. Our team will reach out shortly.",
                    { duration: 6000 },
                );
            } else {
                toast.success(
                    "Order inquiry submitted — our laboratory support team will be in touch.",
                    { duration: 6000 },
                );
            }
            setSubmitted(true);
            clear();
            setForm({ name: "", phone: "", email: "", city: "", notes: "", company: "" });
        } catch (err) {
            console.error(err);
            toast.error("Couldn't submit your order. Please try again or contact us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center" data-testid="checkout-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-400/20 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-7 h-7 text-emerald-300" />
                </div>
                <h1 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">
                    Order inquiry <span className="text-gradient">submitted.</span>
                </h1>
                <p className="text-slate-400 max-w-xl mx-auto leading-relaxed">
                    Your order inquiry has been securely submitted to our laboratory support team. We'll review the details and respond to your email shortly.
                </p>
                <div className="mt-10">
                    <Button asChild className="rounded-full bg-indigo-500 hover:bg-indigo-400 px-7 h-11 btn-glow">
                        <Link to="/products" data-testid="success-continue-browsing">Continue browsing</Link>
                    </Button>
                </div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center" data-testid="empty-checkout">
                <div className="w-16 h-16 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-6">
                    <ShoppingBag className="w-7 h-7 text-slate-500" />
                </div>
                <h1 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">Your cart is empty.</h1>
                <p className="text-slate-400 max-w-lg mx-auto">Browse the catalog to add research compounds.</p>
                <div className="mt-8">
                    <Button asChild className="rounded-full bg-indigo-500 hover:bg-indigo-400 px-6 h-11 btn-glow">
                        <Link to="/products" data-testid="empty-checkout-cta">Continue browsing</Link>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16" data-testid="checkout-page">
            <Link to="/products" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-indigo-400 mb-8 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Continue browsing
            </Link>

            <header className="mb-12 max-w-3xl">
                <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-3">Checkout</p>
                <h1 className="text-4xl md:text-5xl font-light tracking-[-0.03em]">
                    Submit your <span className="text-gradient">order inquiry.</span>
                </h1>
                <p className="mt-5 text-slate-400 leading-relaxed">
                    Enter your details below — your inquiry is sent directly to our laboratory support team, who will respond by email.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <form onSubmit={onSubmit} className="lg:col-span-3 space-y-5" data-testid="checkout-form">
                    {/* Honeypot — invisible to humans. */}
                    <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-7 space-y-5">
                        <h2 className="text-xl font-medium text-white">Your details</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">Name</label>
                                <Input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Full Name"
                                    data-testid="checkout-name-input"
                                    className="bg-white/[0.03] border-white/10 text-white h-11"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">Phone Number</label>
                                <Input
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="+1 555 000 0000"
                                    data-testid="checkout-phone-input"
                                    className="bg-white/[0.03] border-white/10 text-white h-11"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">Email</label>
                                <Input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    data-testid="checkout-email-input"
                                    className="bg-white/[0.03] border-white/10 text-white h-11"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">City</label>
                                <Input
                                    name="city"
                                    value={form.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    data-testid="checkout-city-input"
                                    className="bg-white/[0.03] border-white/10 text-white h-11"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">
                                Order Notes <span className="text-slate-600 normal-case tracking-normal">(optional)</span>
                            </label>
                            <Textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleChange}
                                placeholder="Anything we should know about your order."
                                data-testid="checkout-notes-input"
                                className="bg-white/[0.03] border-white/10 text-white min-h-[100px]"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="submit-order-button"
                        className="w-full rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-12 btn-glow text-base"
                    >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? "Submitting…" : "Submit Order Inquiry"}
                    </Button>
                    <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                        For Research Purposes Only · Your order inquiry will be securely submitted to our laboratory support team.
                    </p>
                </form>

                <aside className="lg:col-span-2">
                    <div className="rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-7 sticky top-24" data-testid="checkout-summary">
                        <h2 className="text-lg font-medium text-white mb-5">Order summary</h2>
                        <div className="space-y-3.5 mb-5 max-h-[380px] overflow-y-auto pr-1">
                            {items.map((item) => (
                                <div key={item.id} className="flex gap-3 items-center" data-testid={`summary-item-${item.id}`}>
                                    <div className="w-12 h-12 rounded-lg bg-[#05050A] shrink-0 overflow-hidden flex items-center justify-center">
                                        <img
                                            src={item.image || VIAL_IMAGE}
                                            alt=""
                                            className={`w-full h-full object-contain p-1 ${item.id === "5-amino-1mq" ? "thumb-tone-cool" : ""}`}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-white truncate">{item.name}</p>
                                        <p className="text-xs text-slate-500">{item.dosage} · ×{item.quantity}</p>
                                    </div>
                                    <p className="text-sm text-white">${(item.price * item.quantity).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                        {/* Discount progress / congrats banner */}
                        {items.length > 0 && (
                            <div
                                data-testid={discountApplied ? "checkout-discount-applied" : "checkout-discount-progress"}
                                className={`mb-4 rounded-xl px-3.5 py-2.5 text-[11px] leading-snug ${
                                    discountApplied
                                        ? "border border-emerald-400/35 bg-emerald-400/10 text-emerald-200"
                                        : "border border-indigo-400/30 bg-indigo-500/10 text-indigo-100"
                                }`}
                            >
                                {discountApplied
                                    ? "🎉 Congratulations! You've unlocked 10% OFF + FREE Shipping!"
                                    : <>You're only <span className="font-semibold text-white">${amountToDiscount.toFixed(2)}</span> away from saving 10%!</>}
                            </div>
                        )}

                        <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
                            <div className="flex justify-between text-slate-400">
                                <span>Subtotal</span>
                                <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                            </div>
                            {discountApplied && (
                                <div className="flex justify-between" data-testid="checkout-discount-row">
                                    <span className="text-emerald-300/90">Discount (10%)</span>
                                    <span className="text-emerald-300">−${discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-slate-400">
                                <span>Shipping</span>
                                <span
                                    data-testid="checkout-shipping"
                                    className={freeShippingApplied ? "text-emerald-300" : ""}
                                >
                                    {items.length === 0
                                        ? "—"
                                        : freeShippingApplied
                                            ? "Free"
                                            : `$${shippingAmount.toFixed(2)}`}
                                </span>
                            </div>
                            <div className="flex justify-between text-white font-medium text-base pt-3 border-t border-white/5">
                                <span>Total</span>
                                <span data-testid="checkout-total">${grandTotal.toFixed(2)}</span>
                            </div>
                            {discountApplied && (
                                <p
                                    data-testid="checkout-savings-message"
                                    className="text-[12px] text-emerald-300/95 text-center pt-1"
                                >
                                    🎉 You saved ${discountAmount.toFixed(2)} today!
                                </p>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Checkout;
